import { c as _c } from "react/compiler-runtime";
import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { FaroTextButton } from "@components/common/faro-text-button";
import { deleteProjectSnapshot } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { bulkActionNameSelector, selectedEntitiesSelector, shouldShowSuccessDialogSelector } from "@store/table/table-selector";
import { setBulkActionName, updateOne } from "@store/table/table-slice";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect, useState } from "react";
import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";
import { Text, DeleteIcon } from "@flat-ui";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { useDeleteSnapshotsBulkDialogContent } from "@pages/project-details/project-snapshots/bulk-actions/bulk-delete-snapshots-helper";
import { isUserSnapshotAdmin } from "@utils/permission-control/snapshot-permission-control";
import { selectedProjectIdSelector } from "@store/projects/projects-selector";
import { assert } from "@faro-lotv/foundation";

/** Show delete snapshots button and dispatch action where the bulk dialog will act accordingly */
export function BulkDeleteSnapshots() {
  const $ = _c(28);
  const projectId = useAppSelector(selectedProjectIdSelector);
  assert(projectId, "BulkDeleteSnapshots: project context is undefined, but required in this case");
  const dispatch = useAppDispatch();
  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const shouldShowSuccessDialog = useAppSelector(shouldShowSuccessDialogSelector);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = selectedEntitiesSelector("snapshots");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const selectedEntities = useAppSelector(t0);
  const {
    bulkDialogContent
  } = useDeleteSnapshotsBulkDialogContent();
  const mode = useBulkActionsMode();
  let t1;
  let t2;
  if ($[1] !== shouldShowSuccessDialog) {
    t1 = () => {
      if (shouldShowSuccessDialog) {
        setShouldShowDialog(false);
      }
    };
    t2 = [shouldShowSuccessDialog];
    $[1] = shouldShowSuccessDialog;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== dispatch || $[5] !== selectedEntities) {
    t3 = function onDeleteSnapshotClick() {
      dispatch(setBulkActionName("deleteSnapshots"));
      for (const entity of selectedEntities) {
        const hasUserValidRole = isUserSnapshotAdmin(entity);
        if (!hasUserValidRole) {
          dispatch(updateOne({
            id: entity.id,
            changes: {
              status: "not-allowed",
              message: "You are not allowed to delete this snapshot from workspace"
            }
          }));
        }
      }
      setShouldShowDialog(true);
    };
    $[4] = dispatch;
    $[5] = selectedEntities;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const onDeleteSnapshotClick = t3;
  let t4;
  if ($[7] !== mode) {
    t4 = getBulkActionButtonConfig(mode, "Delete snapshot from workspace");
    $[7] = mode;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const config = t4;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <DeleteIcon sx={{
      width: "18px",
      height: "18px"
    }} />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== config.shouldShowText) {
    t6 = config.shouldShowText && <Text variant="buttonM">Delete</Text>;
    $[10] = config.shouldShowText;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== config.rippleColor || $[13] !== config.sx || $[14] !== config.tooltipText || $[15] !== onDeleteSnapshotClick || $[16] !== t6) {
    t7 = <FaroTextButton onClick={onDeleteSnapshotClick} tooltipText={config.tooltipText} startIcon={t5} rippleColor={config.rippleColor} sx={config.sx}>{t6}</FaroTextButton>;
    $[12] = config.rippleColor;
    $[13] = config.sx;
    $[14] = config.tooltipText;
    $[15] = onDeleteSnapshotClick;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== bulkActionName || $[19] !== bulkDialogContent || $[20] !== dispatch || $[21] !== projectId || $[22] !== selectedEntities || $[23] !== shouldShowDialog) {
    t8 = bulkActionName === "deleteSnapshots" && shouldShowDialog && <FaroBulkDialog dialogContent={bulkDialogContent} onClose={() => setShouldShowDialog(false)} trackingEvent={BulkActionEvents.bulkDeleteSnapshots} bulkActionCallback={snapshot => dispatch(deleteProjectSnapshot({
      projectId,
      snapshotId: snapshot.id
    })).unwrap()} selectedEntities={selectedEntities} uniqueIdKey="id" tableDataType="snapshots" dialogVariant="danger" />;
    $[18] = bulkActionName;
    $[19] = bulkDialogContent;
    $[20] = dispatch;
    $[21] = projectId;
    $[22] = selectedEntities;
    $[23] = shouldShowDialog;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t7 || $[26] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  return t9;
}